import { DataSheet } from '@lune-fe/dapi-models'
import { Button, LoadingWrapper, Table, Tooltip } from '@lune-fe/lune-ui-lib'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { Divider } from '@mui/material'
import Box from '@mui/material/Box'
import { flatten } from 'lodash'
import moment from 'moment/moment'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import useDataSheets from 'hooks/useDataSheets'
import {
    DataSheetType,
    dataSheetTypeConversion,
    downloadSheet,
} from 'views/CalculateEmissions/Shared/utils'

const FIRST_COL_SIZE = 4

const formatDate = (date: string): string => {
    return moment(date).format('ll')
}

const formatSheetType = (type: DataSheetType, options: DataSheet['options']): string => {
    const cleanedType = type ?? 'unknown'

    if (cleanedType === 'logistics_user_template' && options && 'templateName' in options) {
        return options.templateName
    }

    return dataSheetTypeConversion[cleanedType]
}

const SheetsTable = ({
    type,
}: {
    type: 'fintech' | 'logistics' | 'rfq' | 'logistics_user_template'
}) => {
    const { loading, paginatedDataSheets, hasNextPage, fetchNextPage, refetch } =
        useDataSheets(type)
    const { sheetId } = useParams<{ sheetId?: string }>()
    useEffect(() => {
        refetch()
    }, [type])

    const dataSheets = useMemo(
        () => flatten((paginatedDataSheets?.pages ?? []).map(({ data }) => data)),
        [paginatedDataSheets],
    )

    useEffect(() => {
        if (!sheetId) {
            return
        }

        downloadSheet(sheetId, type)
    }, [sheetId])

    return dataSheets.length ? (
        <>
            <Divider />
            <LoadingWrapper loading={loading}>
                {dataSheets.length > 0 && (
                    <Table.TableContainer>
                        <Table sx={{ minWidth: 650 }}>
                            <Table.TableHead>
                                <Table.TableRow>
                                    <Table.TableCell colSize={FIRST_COL_SIZE}>
                                        Upload date
                                    </Table.TableCell>
                                    {type !== 'fintech' && type !== 'rfq' && (
                                        <Table.TableCell>Type</Table.TableCell>
                                    )}
                                    {type === 'rfq' && <Table.TableCell>Mapping</Table.TableCell>}
                                    <Table.TableCell>Status</Table.TableCell>
                                    <Table.TableCell align="right" alignEnd>
                                        # of calculations
                                    </Table.TableCell>
                                    <Table.TableCell align="right" alignEnd>
                                        # of errors
                                    </Table.TableCell>
                                    <Table.TableCell align="right" alignEnd>
                                        tCO₂
                                    </Table.TableCell>
                                    <Table.TableCell align="right" alignEnd />
                                </Table.TableRow>
                            </Table.TableHead>
                            <Table.TableBody>
                                {dataSheets.map((sheet, i) => (
                                    <Table.TableRow key={i}>
                                        <Table.TableCell colSize={FIRST_COL_SIZE}>
                                            <DescriptionOutlinedIcon sx={{ mr: 3 }} />
                                            <span>{formatDate(sheet.createdAt)}</span>
                                        </Table.TableCell>
                                        {type !== 'fintech' && type !== 'rfq' && (
                                            <Table.TableCell>
                                                {formatSheetType(sheet.type, sheet.options)}
                                            </Table.TableCell>
                                        )}
                                        {type === 'rfq' && (
                                            <Table.TableCell
                                                sx={{
                                                    '> div': {
                                                        maxWidth: '150px',
                                                        overflow: 'hidden',
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        maxWidth: '100%',
                                                        whiteSpace: 'nowrap',
                                                        textOverflow: 'ellipsis',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {sheet.options
                                                        ? 'templateName' in sheet.options
                                                            ? sheet.options.templateName
                                                            : sheet.options.originalFileName
                                                        : ''}
                                                </Box>
                                            </Table.TableCell>
                                        )}
                                        <Table.TableCell>
                                            {sheet.processedAt ? 'Completed' : 'Processing'}
                                        </Table.TableCell>
                                        <Table.TableCell align="right" alignEnd>
                                            {sheet.processedAt ? sheet.numCalculations : '-'}
                                        </Table.TableCell>
                                        <Table.TableCell align="right" alignEnd>
                                            {sheet.processedAt ? sheet.numErrors : '-'}
                                        </Table.TableCell>
                                        <Table.TableCell align="right" alignEnd>
                                            {sheet.processedAt ? sheet.quantity : '-'}
                                        </Table.TableCell>
                                        <Table.TableCell align="right" alignEnd>
                                            {sheet.processedAt ? (
                                                <Button
                                                    variant="text"
                                                    iconButton
                                                    rightIcon={<FileDownloadOutlinedIcon />}
                                                    onClick={() => downloadSheet(sheet.id, type)}
                                                />
                                            ) : (
                                                <Tooltip
                                                    placement="top"
                                                    title="File is being processed"
                                                >
                                                    <Button
                                                        variant="text"
                                                        disabled
                                                        iconButton
                                                        rightIcon={<FileDownloadOutlinedIcon />}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Table.TableCell>
                                    </Table.TableRow>
                                ))}
                            </Table.TableBody>
                        </Table>
                    </Table.TableContainer>
                )}
            </LoadingWrapper>

            {hasNextPage && (
                <Button
                    leftIcon={<ArrowDownwardIcon />}
                    variant="outlined"
                    onClick={() => fetchNextPage()}
                >
                    Load more
                </Button>
            )}
        </>
    ) : (
        <></>
    )
}

export default SheetsTable
